<script setup lang="ts">
import { computed, type VNode, toRef } from 'vue'
import UiNavLink from '../UiNavLink.vue'
import UiIcon, { type UiIconProps } from '../UiIcon.vue'
import UiText from '../UiText.vue'
import { useButtonBase } from './composables/useButtonBase'
import { useButtonToggle } from './composables/useButtonToggle'
import { 
    getVariantClasses, 
    getTransitionClasses, 
    getBackgroundHoverClasses, 
    getArrowClasses,
    getEffectiveHoverStyle,
    getPressedClasses,
    getActivePressedClasses,
} from './utils/buttonClasses'
import type { ButtonBaseProps, ButtonToggleProps, ButtonJustify } from './types'

export interface UiStandardButtonProps extends ButtonBaseProps, ButtonToggleProps {
    /** Button size */
    size?: 'xs' | 'sm' | 'md' | 'lg'
    /** Text label */
    label?: string
    /** Icon name to show before label */
    icon?: UiIconProps['name']
    /** Show arrow icon after label */
    showArrow?: boolean
    /** Content justification */
    justify?: ButtonJustify
}

const props = withDefaults(defineProps<UiStandardButtonProps>(), {
    variant: 'primary',
    size: 'md',
    theme: 'light',
    disabled: false,
    noHover: false,
    justify: 'center',
    type: 'button',
})

const model = defineModel<boolean | undefined>({ default: undefined })

const { isLink, isExternal, effectiveNoHover } = useButtonBase(props)

const { isToggled, isDisabled, handleClick, handleMouseEnter } = useButtonToggle({
    toggle: props.toggle,
    value: props.value,
    disabled: toRef(props, 'disabled'),
    modelValue: model,
})

const effectiveVariant = computed(() => {
    if (isToggled.value && props.activeVariant) {
        return props.activeVariant
    }
    return props.variant
})

const effectiveHoverStyle = computed(() => getEffectiveHoverStyle(props.hoverStyle, effectiveVariant.value))

const dynamicClasses = computed(() => {
    const noHover = effectiveNoHover.value || props.noHover || isToggled.value

    const styleClasses = isToggled.value
        ? getPressedClasses({ variant: effectiveVariant.value, theme: props.theme })
        : getVariantClasses({
            variant: effectiveVariant.value,
            theme: props.theme,
            disabled: isDisabled.value,
        })

    const transitionClasses = getTransitionClasses({
        variant: effectiveVariant.value,
        hoverStyle: effectiveHoverStyle.value,
        theme: props.theme,
        noHover,
    })

    const backgroundHoverClasses = getBackgroundHoverClasses({
        variant: effectiveVariant.value,
        hoverStyle: effectiveHoverStyle.value,
        theme: props.theme,
        noHover,
    })

    const activePressedClasses = !props.toggle && !props.value
        ? getActivePressedClasses({ variant: effectiveVariant.value, theme: props.theme })
        : []

    return [
        {
            ...styleClasses,
            'gap-1 py-0.5 px-2.5 min-h-6': props.size === 'xs',
            'gap-1 py-1 px-3 min-h-8': props.size === 'sm',
            'gap-1.5 py-2 px-4 min-h-11': props.size === 'md',
            'gap-1.5 py-2.5 px-4 min-h-11': props.size === 'lg',
            'justify-between': props.justify === 'between',
            'justify-center text-center': props.justify === 'center',
            'justify-start': props.justify === 'start',
            'cursor-pointer': !isDisabled.value,
            [props.class || '']: Boolean(props.class),
        },
        transitionClasses,
        backgroundHoverClasses,
        activePressedClasses,
    ]
})

const arrowClasses = computed(() => getArrowClasses({
    variant: effectiveVariant.value,
    hoverStyle: effectiveHoverStyle.value,
    theme: props.theme,
}))

defineSlots<{
    before_label: () => VNode
    after_label: () => VNode
}>()
</script>

<template>
    <component 
        :is="isLink ? UiNavLink : 'button'"
        :to="isLink && !isExternal ? props.href : undefined"
        :href="isLink ? props.href : undefined"
        :external="isExternal"
        :target="isLink ? props.target : undefined"
        :rel="isLink ? props.rel : undefined"
        :allow-referrer="isLink ? props.allowReferrer : undefined"
        :type="!isLink ? props.type : undefined"
        :disabled="!isLink && isDisabled"
        :aria-pressed="(props.toggle || props.value !== undefined) ? isToggled : undefined"
        class="group/ui-button inline-flex items-center rounded-md font-semibold text-se-dark se-max-w-full-nested"
        :class="dynamicClasses"
        @click="handleClick"
        @mouseenter="handleMouseEnter"
    >
        <!-- Before label slot with optional icon -->
        <slot name="before_label">
            <UiIcon v-if="props.icon" :name="props.icon" :class="props.size === 'xs' ? 'w-3.5 h-3.5' : 'w-4 h-4'" />
        </slot>

        <!-- Label -->
        <UiText
            v-if="props.label"
            as="button"
            :variant="props.size === 'xs' ? 'override' : 'button'"
            :class="props.size === 'xs' ? 'text-xs font-semibold leading-none' : undefined"
            no-wrap
            :text="label"
        />

        <!-- Arrow icon -->
        <UiIcon 
            v-if="showArrow" 
            name="arrow-right"
            :class="arrowClasses"
        />

        <!-- After label slot -->
        <slot name="after_label" />
    </component>
</template>
